import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { auth } from '@/services/firebase';
import {
  getInstagramConnection,
  disconnectInstagram,
  syncInstagramContent,
  getUserContents,
} from '@/services/instagram';
import type { ConnectedPlatform, PlatformContent } from '@/types';

export const useInstagram = () => {
  const { user } = useAuth();
  const [connection, setConnection] = useState<ConnectedPlatform | null>(null);
  const [contents, setContents] = useState<PlatformContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConnection = useCallback(async () => {
    if (!user) {
      setConnection(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const conn = await getInstagramConnection(user.uid);
      setConnection(conn);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch Instagram connection');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchContents = useCallback(async () => {
    if (!user) {
      setContents([]);
      return;
    }

    try {
      const data = await getUserContents(user.uid);
      setContents(data);
    } catch (err) {
      console.error('Failed to fetch contents:', err);
    }
  }, [user]);

  useEffect(() => {
    fetchConnection();
  }, [fetchConnection]);

  useEffect(() => {
    if (connection?.status === 'active') {
      fetchContents();
    }
  }, [connection, fetchContents]);

  const connect = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');

    // Get Firebase ID token and pass as query param (redirect flow can't use headers)
    const idToken = await currentUser.getIdToken();
    const origin = encodeURIComponent(window.location.origin);
    window.location.href = `https://klipr.it/api/instagram/auth/start?token=${encodeURIComponent(idToken)}&origin=${origin}`;
  };

  const disconnect = async () => {
    if (!connection) return;

    try {
      await disconnectInstagram(connection.id);
      setConnection(null);
      setContents([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disconnect Instagram');
      throw err;
    }
  };

  const sync = async () => {
    try {
      setSyncing(true);
      setError(null);
      const result = await syncInstagramContent();
      await fetchContents();
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync content');
      throw err;
    } finally {
      setSyncing(false);
    }
  };

  return {
    connection,
    contents,
    loading,
    syncing,
    error,
    connect,
    disconnect,
    sync,
    refresh: fetchConnection,
  };
};
